/**
 * Playbooks send PRD U2 (plan Key Technical Decision 10) — the ONE pure
 * classifier over a sequence's `subject`/`body` text that reports every
 * `{{...}}` occurrence a rep needs to fix before send: a Gong-spelled token
 * with NO Terra equivalent, an unsupported Handlebars conditional, or a plain
 * typo.
 *
 * "with no Terra equivalent" is the prod-QA correction of 2026-09-16 (Fix 4):
 * a mappable Gong token is an alias, normalized before any surface sees it —
 * see `copy-generation-rules.ts#normalizeGongTokensInSteps`.
 *
 * Widened 2026-09-16 (playbooks-widen-copy-defect-detection) to also catch
 * placeholder-shaped copy that is NOT `{{...}}` syntax at all — a single
 * brace (`{first_name}`), a double bracket (`[[Company]]`), an angle bracket
 * around a non-HTML word (`<Acme>`) — including one the rich editor
 * entity-escaped to `&lt;Acme&gt;` — a dead `href=""`/`href="#"`, or
 * rep-facing scaffolding a draft never got cleaned up (`Opener:`, `Ask:`,
 * `[If interested...]`). See {@link scanNonBraceOccurrences}. These are
 * DELIBERATELY kept as distinct `kind`s from `unknown` (a `{{...}}` typo) —
 * `send-payload.ts`'s send-blocking `unknownTokens` list is `{{...}}`-only and
 * untouched by this widening; the new kinds ride the same classifier/badge/
 * report plumbing but are never folded into that list (see
 * `MemberResolution.nonBraceDefects`).
 *
 * A CLAY-OWNED token produces NO FLAG (2026-09-16). `{{Company Name}}` used to
 * get its own `company_name` kind whose badge read "Company name is set by
 * Clay — remove {{Company Name}}", on the reasoning that Terra could not
 * resolve it and so a rep had to delete it. Clay's V3 `Render copy` node fills
 * exactly `CLAY_OWNED_TOKENS` deterministically and hard-stops the send if it
 * cannot, so the token is SUPPORTED — telling a rep to remove it would be
 * telling them to delete something that works. The kind is removed rather than
 * kept-but-unproduced: `sequenceTokenFlags` is the only producer, the flags are
 * always DERIVED at request time and never stored, so there is no persisted
 * row or in-flight response that could still carry one. A TYPO of a Clay-owned
 * token (`{{Companny Name}}`) is still `unknown`, which is the property that
 * keeps the allowlist from swallowing real defects.
 *
 * The two rules above COMPOSE, and that composition is the whole story for a
 * legacy Gong sequence: `{{sender.first_name}}` is a `GONG_MERGE_TOKEN_MAP`
 * alias, so every read path normalizes it to `{{Sender First Name}}`, which is
 * Clay-owned — so it is not flagged here, and downstream it is `deferred`
 * (passed to Clay literally) rather than resolved with Terra's guess or
 * counted a copy defect.
 *
 * Pure, DOM-free, browser-safe — same posture as `sequence-readiness.ts`
 * (`rungReadinessReasons`, `notReadyBadgeText`) and `step-content-rules.ts`.
 * `gongStepDescriptor` is an opaque snapshot field (plan Key Decision 12);
 * these flags are always DERIVED from `subject`/`body` text, never stored.
 * One module feeds the step badge, the rail card, the library editor, the
 * server resolution report (U5), and U9's AI-generation validator — so none
 * of those surfaces can disagree about the same input.
 *
 * No zod, no core imports — only `COPY_VARIABLE_FIELDS` labels from
 * `_shared/launcher-copy-tokens.ts`, the Gong vocabulary from
 * `copy-generation-rules.ts` (itself isomorphic), and the HTML tag vocabulary
 * from `./html-format` (also isomorphic, zero imports).
 *
 * That tag vocabulary is TWO sets, not one, and the distinction is the point
 * (C2, 2026-09-17). `containsRealHtmlTags`'s narrow whitelist answers "should
 * the RICH editor own this body?" and has two consumers that depend on its
 * exact shape (`gongStepBodyFormat`, `stepBlock`), so it is deliberately NOT
 * widened. The placeholder scan asks a different question — "is `<tbody>`
 * markup, or did a rep type a placeholder?" — and for that the whitelist was
 * too narrow: an ordinary Gong/Outlook table body reported `<table>`,
 * `<tbody>`, `<tr>` and `<td>` as four placeholders a rep was told to fix.
 * So the scan uses the broader `EMAIL_HTML_TAG_NAMES`/`isEmailHtmlTagName`
 * (every tag name real email HTML uses, minus the tag-shaped words that are
 * plausible rep placeholders — `<Title>` is a job title), and the narrow
 * whitelist keeps its own two consumers unchanged. Both live in
 * `html-format.ts` so neither is ever copied.
 *
 * The scan also runs on a TEXT PROJECTION of the body rather than the raw
 * HTML (C5) — see {@link toTextProjection}. That projection is produced by ONE
 * left-to-right pass which also hands back the body's quoted ATTRIBUTE VALUES
 * and its parsed `href` attributes, because a projection of what the prospect
 * READS necessarily drops the attributes a defect can hide in. Which source
 * owns which kind is the table in {@link scanNonBraceOccurrences}; the pass
 * itself is linear in the body's length (round-3 F4).
 */
import { COPY_VARIABLE_FIELDS, isClayOwnedToken } from '../_shared/launcher-copy-tokens';
import {
  GONG_MERGE_TOKEN_MAP,
  GONG_TOKENS_WITHOUT_TERRA_EQUIVALENT,
  conditionalMarkersIn,
} from './copy-generation-rules';
import { containsRealHtmlTags, emailHtmlTagSpacing, isEmailHtmlTagName } from './html-format';

/** The five non-`{{}}` placeholder/scaffolding defect classes this module also detects. */
export type NonBraceDefectKind =
  | 'single_brace'
  | 'double_bracket'
  | 'angle_bracket'
  | 'dead_href'
  | 'scaffolding';

/**
 * THE one kind→delimiter table for a non-`{{}}` defect (Devin D6). Before
 * this export, three call sites each hardcoded the same five-way mapping —
 * `describeNonBraceDefect` (`send-payload.ts`), `nonBraceDisplayToken`
 * (`dispatch-inspector-logic.ts`), and this file's own `tokenFlagBadgeText`
 * — and a fourth delimiter change would have needed three simultaneous edits
 * to stay in sync. `single_brace`/`double_bracket`/`angle_bracket` get
 * re-wrapped in the delimiters a rep actually typed; `dead_href`/
 * `scaffolding` come back unwrapped — their matched text already reads
 * naturally (a full `href="#"`, a `Opener:` label) and re-wrapping it would
 * only add noise. Each call site keeps its OWN surrounding prose (this is
 * only the token-to-delimiters half).
 */
export function wrapNonBraceToken(kind: NonBraceDefectKind, token: string): string {
  switch (kind) {
    case 'single_brace':
      return `{${token}}`;
    case 'double_bracket':
      return `[[${token}]]`;
    case 'angle_bracket':
      return `<${token}>`;
    case 'dead_href':
    case 'scaffolding':
      return token;
    default: {
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}

// `'company_name'` is deliberately GONE: `{{Company Name}}` is now a
// Clay-OWNED token whose value Clay fills at send, so it is a supported token
// rather than a defect. `isClayOwnedToken` returns it unflagged in
// `classifyKey` below, and the badge line that told a rep to remove it has
// been dropped with it — telling someone to delete a token that now works
// would be actively wrong.
export type TokenFlagKind = 'gong_unmapped' | 'conditional' | 'unknown' | NonBraceDefectKind;

export interface TokenFlag {
  kind: TokenFlagKind;
  /**
   * The token's raw spelling, no braces: the Gong dotted key
   * (`recipient.first_name`) or the typo as written (`Frist Name`). For
   * `kind: 'conditional'` this is a fixed
   * placeholder (`'conditional'`) — conditional occurrences collapse into
   * ONE flag per sequence regardless of how many distinct markers
   * (`#if`/`else`/`/if`) appear, because there is nothing token-specific to
   * report (see {@link tokenFlagBadgeText}'s "Unsupported conditional").
   * For the five non-`{{}}` `NonBraceDefectKind`s, see
   * {@link NonBraceOccurrence.token}'s doc for the delimiter convention.
   */
  token: string;
  /** Every step position where this token occurs, ascending, deduped. */
  stepPositions: number[];
  /**
   * Always `null` since Fix 4 (a Gong token with a Terra equivalent is
   * normalized away before it can be flagged); kept because it is on the wire
   * — `PlaybooksTokenFlagSchema`, `schemas.ts`.
   */
  terraEquivalent: string | null;
}

/** The subset of a `SequenceStep` this classifier reads — subject/body only, same shape as `StepTokenSource`. */
export interface StepFlagSource {
  position: number;
  subject?: string;
  body?: string;
}

/** Every `{{...}}` occurrence, capturing the inner text verbatim (untrimmed) — tolerant of any inner content, unlike `GONG_TOKEN_PATTERN`. */
const TOKEN_OCCURRENCE_PATTERN = /\{\{([^}]*)\}\}/g;

/** Gong's own dotted `object.field` spelling, e.g. `account.Type` — a defensive net for a token not yet catalogued in `GONG_TOKENS_WITHOUT_TERRA_EQUIVALENT`. */
const DOTTED_GONG_KEY_PATTERN = /^[A-Za-z]+\.[A-Za-z0-9_]+$/;

const KNOWN_TERRA_LABELS: ReadonlySet<string> = new Set(COPY_VARIABLE_FIELDS.map((f) => f.label));

/** Fixed dedupe/display token for every conditional occurrence — see {@link TokenFlag.token}'s doc. */
const CONDITIONAL_TOKEN = 'conditional';

// Non-`{{}}` defect classes (playbooks-widen-copy-defect-detection).
//
// Validated empirically against a Clay agent on a 20-step matrix (20/20) —
// these five patterns are known-real authoring mistakes, not speculative.

/**
 * A single-brace placeholder (`{first_name}`, `{time slot 1}`). Lookaround on
 * BOTH sides of each brace (`(?<!\{)`/`(?!\{)` on the open, `(?!\})` on the
 * close) is load-bearing: without it, a real `{{First Name}}` token reads as
 * an opening `{` immediately followed by a `{`, so a naive `\{([^{}]+)\}`
 * would match the INNER `{First Name}` and misreport a legitimate merge
 * token as a single-brace defect.
 */
const SINGLE_BRACE_RE = /(?<!\{)\{(?!\{)([^{}]+)\}(?!\})/g;

/** A double-bracket placeholder (`[[Company]]`, `[[first name]]`). */
const DOUBLE_BRACKET_RE = /\[\[([^[\]]+)\]\]/g;

/**
 * An angle-bracket placeholder around a bare word/phrase (`<Acme>`,
 * `<Company>`) — deliberately narrow so it cannot match real markup or a
 * comparison: the character class allows only letters, digits, spaces,
 * underscores and hyphens, so anything with `=`, quotes, `/` or `:` (every
 * attribute-bearing or self-closing real tag) never matches at all, and a
 * bare real tag name that DOES match (`<div>`, `<br>`, `<ul>`, `<li>`) is
 * filtered out below via {@link containsRealHtmlTags} — the SAME whitelist
 * `html-format.ts` uses to classify a Gong step body, reused rather than
 * copied a third time. `price < 5` never matches because `<` must be
 * immediately followed by a letter, not a space or digit.
 */
const ANGLE_PLACEHOLDER_RE = /<([A-Za-z][A-Za-z0-9 _-]{0,60})>/g;

/**
 * A dead link destination: an `href` whose value renders as nothing, or as a
 * bare `#`. Deliberately NOT true of `href="#section"` — an in-page anchor
 * jump is not a defect this scans for, only the two
 * `{{Primary Seller Link}}`-style "the rep never came back and filled this in"
 * shapes.
 *
 * Judged on the DECODED value, which is round-3 F9: `href="&#35;"` is the same
 * dead `#` destination as `href="#"`, and the raw-text regex this replaced saw
 * only the second one. It is also judged on a PARSED attribute of a real tag,
 * which fixes the other half of the same inconsistency — the old regex matched
 * `href="#"` anywhere in the raw string, including inside a `<style>` body
 * (`content: 'href="#"'`) where there is no anchor at all, and including prose
 * a rep wrote ABOUT a link.
 */
function isDeadHrefValue(decodedValue: string): boolean {
  const trimmed = decodedValue.trim();
  return trimmed === '' || trimmed === '#';
}

/**
 * Rep-facing planning scaffolding left in copy: a line-leading label
 * (`Opener:`, `Ask:`, `FAQ:`, `Notes:`, `Talk track:`) or a bracketed branch
 * instruction (`[If interested, ...]`). `^` requires the label to START the
 * line (after only leading whitespace) so an ordinary sentence that happens
 * to CONTAIN the word "notes" or "ask" is never flagged.
 */
const SCAFFOLDING_LABEL_RE = /^[ \t]*(Opener|Ask|FAQ|Notes|Talk track)\s*:/gim;
// One nested `[...]` level is allowed (`[If they mention [competitor], pivot]`), but every
// class stays `[`-free: that is what keeps an unclosed `[If ` run from rescanning to the end.
const SCAFFOLDING_BRACKET_RE = /\[If\b[^\][\n]*(?:\[[^\][\n]*\][^\][\n]*)*\]/gi;

// The TEXT PROJECTION (C5, playbooks-widen-copy-defect-detection).
//
// Reps author in a rich editor, so a step body is HTML by the time it reaches
// this scanner — and two whole classes of real defect were invisible against
// the raw string:
//
//   `<p>Opener: Hi Ada</p>`        → nothing (`SCAFFOLDING_LABEL_RE` is
//                                    `^`-anchored, and the `<p>` is in front)
//   `<p>Hello &lt;Company&gt;</p>` → nothing (there is no literal `<` at all)
//
// So the four TEXT kinds (`single_brace`, `double_bracket`, `angle_bracket`,
// `scaffolding`) scan a projection of what the prospect will actually READ.
//
// ROUND 3 (F5) closed the hole that partition opened. The projection deletes a
// known email tag whole, attributes and all, so `<a href="{meeting_link}">`
// was reported by NOTHING: the text source no longer contained it, and the
// old raw-text `dead_href` regex matched neither the placeholder nor the
// unresolved destination. Warn-only means the warning IS the product, so the
// fix is a SECOND SOURCE — the body's quoted attribute values — rather than a
// second full scan of the raw string, which would double-report every ordinary
// placeholder (a rep clearing a badge that names the same token twice learns
// to distrust the badge).
//
// Both sources come out of ONE left-to-right pass ({@link toTextProjection}),
// and the partition that keeps every occurrence reported EXACTLY once is:
//
//   projection text     → single_brace, double_bracket, angle_bracket,
//                         scaffolding — including the attributes of a tag
//                         whose name is NOT known email HTML, since such a
//                         tag is kept VERBATIM (that is what keeps `<Company>`
//                         findable) and is therefore already text
//   attribute values    → single_brace, double_bracket, angle_bracket — but
//                         ONLY for a tag the projection ELIDED, which is
//                         exactly the complement of the case above
//   parsed `href` attrs → dead_href, on the decoded value (F9)
//
// The projection pass is also LINEAR (F4); the `matchAll` patterns that run on its
// output are separate and are not metered. The regexes it replaced (`[^>]*>` for a tag,
// and the same shape for a raw-text opening) re-scanned the whole remaining
// suffix at every candidate `<` that never closed: 6/12/24/48 KB of `'<p '`
// measured 2.0/6.7/24.4/86.2 ms, quadrupling per doubling. This runs
// synchronously on every editor keystroke and once per member on send, and
// `SequenceStepSchema` bounds no body length.

/**
 * The START of an HTML-shaped tag, capturing the closing slash and the NAME so
 * a name off the known set can be kept verbatim. STICKY (`y`), not global: it
 * is only ever tested AT a `<` the pass already found, so it can never scan
 * ahead — `price < 5` fails here and the `<` is emitted as the literal text it
 * is. Where the tag ENDS is {@link scanTagAttributes}'s job, not a regex's,
 * because the answer depends on quoting (F7) and must not re-scan (F4).
 */
const TAG_START_RE = /<(\/?)([A-Za-z][A-Za-z0-9]*)/y;

/**
 * A character legal in an attribute NAME, per HTML's own tag-open state:
 * anything but whitespace and the five characters that delimit the name
 * (`"`, `'`, `>`, `/`, `=`).
 */
const ATTR_NAME_CHAR_RE = /[^\s"'>/=]/;

/** Whitespace between a tag's name, its attributes, and its `=` signs. */
const TAG_SPACE_RE = /\s/;

/**
 * Unicode space separators the projection folds to a plain space — round-3 F8.
 *
 * `&nbsp;` and `&#160;` and `&#xA0;` are the SAME character to a recipient, and
 * before this a body warned in the first spelling and was silent in the other
 * two: the named entity happened to decode to an ordinary space while the
 * numeric forms decoded to U+00A0, which `SCAFFOLDING_LABEL_RE`'s `^[ \t]*`
 * lead-in does not accept.
 *
 * NORMALIZING here is deliberate, rather than widening that one rule's
 * whitespace class. The projection is defined as "what the prospect reads",
 * and a non-breaking space reads as a space — so this belongs to the
 * projection, where it holds for every present and future rule at once.
 * Widening `[ \t]*` would have fixed exactly one rule and left the same hole in
 * the other four.
 *
 * It runs AFTER the one-pass entity decode and maps only space separators onto
 * a space, so it can never manufacture a `<` or an `&`: `&amp;lt;` still
 * becomes the literal text `&lt;` and never the character `<`.
 */
const UNICODE_SPACE_SEPARATOR_RE = /[   -   　]/g;

/**
 * Every entity the projection decodes, in ONE alternation — deliberately a
 * single `replace` pass rather than one pass per entity. A single pass never
 * rescans its own output, which is exactly the "decode `&amp;` LAST" property:
 * `&amp;lt;` becomes the literal text `&lt;` (a rep writing ABOUT escaping)
 * and never the character `<` (a placeholder that was never there).
 */
const HTML_ENTITY_RE = /&(?:#(x[0-9A-Fa-f]{1,6}|[0-9]{1,7})|(lt|gt|amp|quot|apos|nbsp));/gi;

const NAMED_ENTITY_VALUES: Readonly<Record<string, string>> = {
  lt: '<',
  gt: '>',
  amp: '&',
  quot: '"',
  apos: "'",
  // Decoded FAITHFULLY, to the character it actually is. Folding it to an
  // ordinary space is {@link UNICODE_SPACE_SEPARATOR_RE}'s job, one step later
  // — which is what makes `&nbsp;`, `&#160;`, `&#xA0;` and a pasted literal
  // U+00A0 all behave identically (F8). Mapping it to `' '` here would have
  // fixed only the named spelling, which is the bug that was found.
  nbsp: ' ',
};

/**
 * Entities decoded (ONE pass, never rescanning its own output — see
 * {@link HTML_ENTITY_RE}) and Unicode space separators folded to a plain space
 * (see {@link UNICODE_SPACE_SEPARATOR_RE}). Applied to the projection as a
 * whole AND to each attribute value, so both sources agree on what a character
 * is. Also the entity/space decoder `plain-text.ts#htmlToPlainText` uses, so
 * the projection the scanner reads and the text Inflection sends decode
 * characters identically.
 */
export function toRenderedText(text: string): string {
  const decoded = text.includes('&')
    ? text.replace(HTML_ENTITY_RE, (whole, numeric: string | undefined, named: string | undefined) => {
      if (named !== undefined) return NAMED_ENTITY_VALUES[named.toLowerCase()] ?? whole;
      if (numeric === undefined) return whole;
      const code = numeric.startsWith('x') || numeric.startsWith('X')
        ? Number.parseInt(numeric.slice(1), 16)
        : Number.parseInt(numeric, 10);
      if (!Number.isFinite(code) || code < 1 || code > 0x10ffff) return whole;
      return String.fromCodePoint(code);
    })
    : text;
  return decoded.replace(UNICODE_SPACE_SEPARATOR_RE, ' ');
}

/** One attribute of one tag, as the pass read it. */
interface TagAttribute {
  /** The attribute name verbatim, e.g. `href` or `HREF` — compare case-insensitively. */
  name: string;
  /** The value, entity-decoded and space-normalized by {@link toRenderedText}. */
  value: string;
  /**
   * The rep's RAW spelling, `name="value"`, quotes and entities exactly as
   * typed — this is what a `dead_href` badge shows, because it is the string
   * the rep has to find in the editor.
   */
  raw: string;
  /** Whether the value was quoted. Only a quoted value is a placeholder source. */
  quoted: boolean;
}

/**
 * Optional work counter for the projection pass: a deterministic stand-in for
 * wall-clock time, so a unit test can assert LINEAR growth without a clock.
 * `steps` is bumped once per character the pass visits or skips over
 * (`indexOf` spans count their length). Production callers pass nothing.
 */
export interface ScanMeter {
  steps: number;
}

interface TagScan {
  /** Index just past the tag's closing `>`, or `-1` when no `>` closes it. */
  end: number;
  attributes: TagAttribute[];
}

/**
 * Reads a tag's attribute region left-to-right from `from` (just past the tag
 * name) and returns where the tag ENDS plus the attributes it carried.
 *
 * QUOTE-AWARE, which is round-3 F7: the `[^>]*>` this replaced ended the tag at
 * the first `>` anywhere, so `<span title="1 > 0">Opener: Hi Ada</span>` ended
 * after `1 >` and left ` 0">Opener:` sitting in the projection — where the
 * `^`-anchored scaffolding rule could never match it, even though the rendered
 * text begins `Opener:`.
 *
 * `end: -1` means there is no `>` closing this tag in the REST of the body, so
 * the caller can stop looking for tags entirely. That is the property that
 * makes the whole projection linear (F4): every character is examined by at
 * most one scan, instead of once per candidate `<` ahead of it.
 */
function scanTagAttributes(html: string, from: number, meter: ScanMeter): TagScan {
  const attributes: TagAttribute[] = [];
  let i = from;

  while (i < html.length) {
    const char = html[i];
    meter.steps += 1;

    if (char === '>') return { end: i + 1, attributes };

    // A quote where an attribute name was expected is malformed markup; skip
    // the quoted span so a `>` inside it still cannot end the tag.
    if (char === '"' || char === "'") {
      const close = html.indexOf(char, i + 1);
      meter.steps += (close === -1 ? html.length : close) - i;
      if (close === -1) return { end: -1, attributes: [] };
      i = close + 1;
      continue;
    }

    // Whitespace, and the `/` of a self-closing tag, are not name characters.
    if (!ATTR_NAME_CHAR_RE.test(char)) {
      i += 1;
      continue;
    }

    let nameEnd = i;
    while (nameEnd < html.length && ATTR_NAME_CHAR_RE.test(html[nameEnd])) nameEnd += 1;
    meter.steps += nameEnd - i;
    const name = html.slice(i, nameEnd);

    let afterName = nameEnd;
    while (afterName < html.length && TAG_SPACE_RE.test(html[afterName])) afterName += 1;
    meter.steps += afterName - nameEnd;
    if (html[afterName] !== '=') {
      // A valueless (boolean) attribute — `<td nowrap>`. Resuming at
      // `afterName` (not `nameEnd`) means the whitespace run just walked is not
      // walked a second time, and it always makes progress: the name was at
      // least one character long.
      i = afterName;
      continue;
    }

    let valueStart = afterName + 1;
    while (valueStart < html.length && TAG_SPACE_RE.test(html[valueStart])) valueStart += 1;
    meter.steps += valueStart - afterName;
    const quote = html[valueStart];

    if (quote === '"' || quote === "'") {
      const close = html.indexOf(quote, valueStart + 1);
      meter.steps += (close === -1 ? html.length : close) - valueStart;
      // An unterminated quoted value swallows the rest of the body, so nothing
      // ahead is a tag either.
      if (close === -1) return { end: -1, attributes: [] };
      const rawValue = html.slice(valueStart + 1, close);
      attributes.push({
        name,
        value: toRenderedText(rawValue),
        raw: `${name}=${quote}${rawValue}${quote}`,
        quoted: true,
      });
      i = close + 1;
      continue;
    }

    let valueEnd = valueStart;
    while (valueEnd < html.length && !TAG_SPACE_RE.test(html[valueEnd]) && html[valueEnd] !== '>') {
      valueEnd += 1;
    }
    meter.steps += valueEnd - valueStart;
    const rawValue = html.slice(valueStart, valueEnd);
    attributes.push({
      name,
      value: toRenderedText(rawValue),
      raw: `${name}=${rawValue}`,
      quoted: false,
    });
    i = valueEnd;
  }

  return { end: -1, attributes: [] };
}

/** The three sources one pass over a body produces — see the section comment above. */
interface ProjectedBody {
  /**
   * What the prospect READS: `<style>`/`<script>` bodies gone, known email tags
   * replaced by the whitespace they render as (a line break, a cell tab, or
   * nothing), entities decoded, space separators normalized.
   *
   * A tag whose name is NOT known email HTML is left VERBATIM — that is the
   * load-bearing half. Stripping `<[^>]+>` wholesale would delete `<Company>`
   * along with `<div>` and blind the angle-bracket scan to the very thing it
   * exists to find.
   */
  text: string;
  /**
   * Every QUOTED attribute value of a tag `text` elided, decoded — the second
   * placeholder source (F5). A tag kept verbatim contributes none, because its
   * attributes are already part of `text`.
   */
  attributeValues: string[];
  /** Every dead `href`, in the rep's raw spelling, document order (F9). */
  deadHrefs: string[];
}

/**
 * ONE left-to-right pass producing all three sources {@link ProjectedBody}
 * documents. Linear in `raw.length`: a text run is found with `indexOf`, a tag
 * is read once by {@link scanTagAttributes}, and a tag that never closes ends
 * the pass rather than being re-examined from the next `<` (F4).
 */
function toTextProjection(raw: string, meter: ScanMeter): ProjectedBody {
  // No `<` at all: there is no tag to end, no attribute to read, and no
  // raw-text block to skip — only the render-level normalization, which a
  // pasted literal U+00A0 still needs (F8).
  if (!raw.includes('<')) return { text: toRenderedText(raw), attributeValues: [], deadHrefs: [] };

  const attributeValues: string[] = [];
  const deadHrefs: string[] = [];
  let out = '';
  let cursor = 0;
  // Only a `<style>`/`<script>` body needs the case-insensitive search, so the
  // lowered copy is built at most once and only when one appears.
  let lowered: string | null = null;

  while (cursor < raw.length) {
    const open = raw.indexOf('<', cursor);
    meter.steps += 1 + (open === -1 ? raw.length : open) - cursor;
    if (open === -1) {
      out += raw.slice(cursor);
      break;
    }
    out += raw.slice(cursor, open);

    TAG_START_RE.lastIndex = open;
    const start = TAG_START_RE.exec(raw);
    if (start === null) {
      // Not a tag at all — a comparison (`price < 5`), a stray `<>`, a comment
      // or doctype opener. The `<` is literal text.
      out += '<';
      cursor = open + 1;
      continue;
    }

    const scan = scanTagAttributes(raw, TAG_START_RE.lastIndex, meter);
    if (scan.end === -1) {
      // No `>` closes this tag anywhere ahead, so no tag can start ahead
      // either: the whole remainder is literal text. Emitting it and STOPPING
      // is what keeps `'<p '.repeat(n)` linear (F4).
      out += raw.slice(open);
      break;
    }

    // `dead_href` is judged on every parsed tag, known name or not — an
    // unknown tag's `href` is still a dead link, and no text kind can collide
    // with `href="…"` (neither brace kind has braces there, and the angle kind
    // rejects any match containing `=`).
    for (const attribute of scan.attributes) {
      if (attribute.name.toLowerCase() === 'href' && isDeadHrefValue(attribute.value)) {
        deadHrefs.push(attribute.raw);
      }
    }

    const spacing = emailHtmlTagSpacing(start[2]);
    if (spacing === null) {
      // NOT known email HTML: kept verbatim so `<Company>` survives — which
      // also means its attribute values are the TEXT source's, not the
      // attribute source's. See the partition in the section comment.
      out += raw.slice(open, scan.end);
      cursor = scan.end;
      continue;
    }

    // The tag is elided, so these values would otherwise be scanned by nothing
    // at all (F5). This is the one source that owns them.
    for (const attribute of scan.attributes) {
      if (attribute.quoted) attributeValues.push(attribute.value);
    }

    const name = start[2].toLowerCase();
    if (start[1] === '' && (name === 'style' || name === 'script')) {
      // A raw-text element's CONTENT is never rep-facing copy: CSS
      // `{color:red}` is not a placeholder, and a `content: 'href="#"'` rule
      // is not a dead link (F9's second half).
      if (lowered === null) {
        meter.steps += raw.length;
        lowered = raw.toLowerCase();
      }
      const closeAt = lowered.indexOf(`</${name}`, scan.end);
      meter.steps += (closeAt === -1 ? raw.length : closeAt) - scan.end;
      if (closeAt === -1) {
        // Unterminated: everything after the opening tag is stylesheet or
        // script source.
        break;
      }
      const closeEnd = raw.indexOf('>', closeAt);
      cursor = closeEnd === -1 ? raw.length : closeEnd + 1;
      continue;
    }

    out += spacing;
    cursor = scan.end;
  }

  return { text: toRenderedText(out), attributeValues, deadHrefs };
}

/** One non-`{{}}` defect occurrence, position-free — the caller attaches a step position. */
export interface NonBraceOccurrence {
  kind: NonBraceDefectKind;
  /**
   * Bare inner text for `single_brace`/`double_bracket`/`angle_bracket` (no
   * delimiters, mirroring {@link TokenFlag.token}'s convention for `{{...}}`
   * kinds); the exact matched text for `dead_href`/`scaffolding`, which read
   * naturally without re-wrapping.
   */
  token: string;
}

/**
 * Every non-`{{}}` placeholder/scaffolding occurrence in `text`, in
 * first-found order (not deduped — the caller dedupes by step/kind/token, see
 * {@link aggregateNonBraceOccurrences}).
 *
 * NEVER emits an occurrence whose `token` is empty (C1,
 * playbooks-widen-copy-defect-detection). `{ }` and `[[ ]]` match their
 * patterns but their inner text trims to `''` — there is no placeholder to
 * NAME, and `PlaybooksNonBraceDefect.token` is `z.string().min(1)`
 * (`schemas.ts`), so emitting one made `PlaybooksSendDefectSchema.safeParse`
 * fail in `send.ts` and the preview/report `.parse` calls 500: one stray `{ }`
 * in one step aborted the WHOLE send instead of skipping one member. The
 * scanner is the only producer of that field, so the invariant belongs here —
 * pinned by the `scanNonBraceOccurrences ⇄ PlaybooksNonBraceDefectSchema`
 * agreement test, which parses every emitted occurrence against the real
 * schema.
 *
 * That invariant holds BY CONSTRUCTION for the attribute-value source round 3
 * added (F5): every kind, from every source, is emitted through the one `push`
 * below, so `<a href="{ }">` can no more emit an empty token than a bare
 * `{ }` in the body can.
 */
export function scanNonBraceOccurrences(text: string, meter: ScanMeter = { steps: 0 }): NonBraceOccurrence[] {
  const found: NonBraceOccurrence[] = [];
  // C1 — an occurrence whose token would be EMPTY is never emitted (every
  // kind goes through this one push, so no pattern can bypass it): `{ }`
  // names no placeholder, and emitting one used to abort the whole send. See
  // this function's doc comment.
  const push = (kind: NonBraceDefectKind, token: string): void => {
    if (token === '') return;
    found.push({ kind, token });
  };

  // C5, refined in round 3 (F5/F9) — ONE pass over the body produces the three
  // sources, and each KIND has exactly one set of sources, so no occurrence is
  // reported twice:
  //
  //   single_brace / double_bracket / angle_bracket → projection text, then
  //     every quoted attribute value of a tag the projection elided
  //   scaffolding                                   → projection text only (a
  //     planning label is a thing the prospect READS; an attribute is not a
  //     line of copy)
  //   dead_href                                     → parsed `href`
  //     attributes, judged on the decoded value
  //
  // See {@link toTextProjection} and the section comment above it.
  const projected = toTextProjection(text, meter);
  const placeholderSources = [projected.text, ...projected.attributeValues];

  for (const source of placeholderSources) {
    for (const match of source.matchAll(SINGLE_BRACE_RE)) {
      push('single_brace', match[1].trim());
    }
  }
  for (const source of placeholderSources) {
    for (const match of source.matchAll(DOUBLE_BRACKET_RE)) {
      push('double_bracket', match[1].trim());
    }
  }
  for (const source of placeholderSources) {
    for (const match of source.matchAll(ANGLE_PLACEHOLDER_RE)) {
      if (containsRealHtmlTags(match[0])) continue;
      const inner = match[1].trim();
      // C2 — a real email tag is not a placeholder. `containsRealHtmlTags`
      // above only covers the narrower GONG-BODY-CLASSIFICATION whitelist (left
      // deliberately un-widened, it has two other consumers); this is the
      // broader `EMAIL_HTML_TAG_NAMES` check that keeps `<table>`, `<tbody>`,
      // `<tr>`, `<td>`, `<hr>`, `<sup>`… out of the results. The FIRST word is
      // what is tested, so `<Company Name>` still flags.
      if (isEmailHtmlTagName(inner.split(/\s+/)[0] ?? '')) continue;
      push('angle_bracket', inner);
    }
  }
  for (const deadHref of projected.deadHrefs) {
    push('dead_href', deadHref.trim());
  }
  for (const match of projected.text.matchAll(SCAFFOLDING_LABEL_RE)) {
    push('scaffolding', `${match[1]}:`);
  }
  for (const match of projected.text.matchAll(SCAFFOLDING_BRACKET_RE)) {
    push('scaffolding', match[0].trim());
  }

  return found;
}

/** One non-`{{}}` defect, folded across steps — the shape `send-payload.ts`'s `MemberResolution.nonBraceDefects` carries. */
export interface NonBraceDefectFlag {
  kind: NonBraceDefectKind;
  token: string;
  /** Every step position where this exact (kind, token) pair occurs, ascending, deduped. */
  stepPositions: number[];
}

/**
 * Folds per-step {@link NonBraceOccurrence} lists into one {@link NonBraceDefectFlag}
 * per distinct `(kind, token)` pair, `stepPositions` ascending — the same
 * dedupe/sort convention {@link sequenceTokenFlags} uses for `{{...}}` kinds.
 * `send-payload.ts#toMemberResolution` calls this to build
 * `MemberResolution.nonBraceDefects` from every step's `nonBraceByField`.
 */
export function aggregateNonBraceOccurrences(
  entries: readonly { position: number; occurrences: readonly NonBraceOccurrence[] }[],
): NonBraceDefectFlag[] {
  const byKey = new Map<string, NonBraceDefectFlag>();
  for (const { position, occurrences } of entries) {
    for (const { kind, token } of occurrences) {
      const key = `${kind}::${token}`;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.stepPositions.includes(position)) existing.stepPositions.push(position);
      } else {
        byKey.set(key, { kind, token, stepPositions: [position] });
      }
    }
  }
  const flags = [...byKey.values()];
  for (const flag of flags) flag.stepPositions.sort((a, b) => a - b);
  return flags;
}

function classifyKey(trimmedKey: string): { kind: TokenFlagKind; terraEquivalent: string | null } | null {
  // A Clay-owned token is a supported, rep-facing token whose VALUE Clay fills
  // at send — checked FIRST, and matched case-insensitively/whitespace-
  // tolerantly by `isClayOwnedToken`, so a rep who typed `{{company name}}`
  // gets the same (no) flag as one who typed it exactly. See the header.
  if (isClayOwnedToken(trimmedKey)) {
    return null;
  }
  // Prod QA, 2026-09-16 (Fix 4) — a `GONG_MERGE_TOKEN_MAP` key is an ALIAS of
  // its Terra token, not a defect: `normalizeGongTokensInSteps` converts it at
  // the one server-side sequence-version read seam
  // (`versioning.ts#loadSequenceVersion`) and on the way into
  // `copy-token-flags.ts`'s resolvers, so by the time any surface sees this
  // copy the Gong spelling is gone and the token resolves like any other.
  //
  // This used to return `gong_unmapped` with a non-null `terraEquivalent` —
  // a badge saying "needs Terra conversion" for something nothing would ever
  // convert, since normalization only ran at Gong IMPORT. Real cost, not
  // cosmetic: the same classification made a send mark every member a copy
  // defect, so a sequence imported before 2026-09-16 sent to nobody. The
  // product owner's ruling is that standard Gong tokens convert
  // automatically, and that a badge alone is not an acceptable answer.
  //
  // Flag-and-leave is UNCHANGED for everything genuinely unconvertible: the
  // `GONG_TOKENS_WITHOUT_TERRA_EQUIVALENT` set (SFDC custom fields — Terra has
  // no equivalent vocabulary and never will), any other dotted
  // `object.field` spelling, and Handlebars conditionals.
  if (trimmedKey in GONG_MERGE_TOKEN_MAP) {
    return null; // an alias of a known Terra token — no flag
  }
  if (GONG_TOKENS_WITHOUT_TERRA_EQUIVALENT.has(trimmedKey) || DOTTED_GONG_KEY_PATTERN.test(trimmedKey)) {
    return { kind: 'gong_unmapped', terraEquivalent: null };
  }
  if (KNOWN_TERRA_LABELS.has(trimmedKey)) {
    return null; // a known Terra token — no flag
  }
  return { kind: 'unknown', terraEquivalent: null };
}

/**
 * Every rep-visible token issue in `steps`, deduped by token (conditionals
 * all collapse into one), `stepPositions` ascending. Known Terra tokens
 * produce no flag. Whitespace inside braces is tolerated (matches
 * `GONG_TOKEN_PATTERN`'s tolerance elsewhere in this codebase).
 */
export function sequenceTokenFlags(steps: readonly StepFlagSource[]): TokenFlag[] {
  const flagsByToken = new Map<string, TokenFlag>();

  for (const step of steps) {
    const text = `${step.subject ?? ''}\n${step.body ?? ''}`;
    if (text.trim() === '') continue;

    const conditionalMarkers = new Set(conditionalMarkersIn(text));

    for (const match of text.matchAll(TOKEN_OCCURRENCE_PATTERN)) {
      const whole = match[0];
      const trimmedKey = (match[1] ?? '').trim();

      let dedupeKey: string;
      let kind: TokenFlagKind;
      let token: string;
      let terraEquivalent: string | null;

      if (conditionalMarkers.has(whole)) {
        dedupeKey = CONDITIONAL_TOKEN;
        kind = 'conditional';
        token = CONDITIONAL_TOKEN;
        terraEquivalent = null;
      } else {
        const classified = classifyKey(trimmedKey);
        if (classified === null) continue; // known Terra token — no flag
        dedupeKey = trimmedKey;
        kind = classified.kind;
        token = trimmedKey;
        terraEquivalent = classified.terraEquivalent;
      }

      const existing = flagsByToken.get(dedupeKey);
      if (existing) {
        if (!existing.stepPositions.includes(step.position)) {
          existing.stepPositions.push(step.position);
        }
      } else {
        flagsByToken.set(dedupeKey, { kind, token, stepPositions: [step.position], terraEquivalent });
      }
    }

    // The five non-`{{}}` classes (playbooks-widen-copy-defect-detection).
    // Keyed `kind::token` — a namespace distinct from the bare `dedupeKey`
    // strings above, so a non-brace token can never collide with a `{{...}}`
    // one even if the raw text happened to match.
    for (const { kind: nonBraceKind, token: nonBraceToken } of scanNonBraceOccurrences(text)) {
      const dedupeKey = `${nonBraceKind}::${nonBraceToken}`;
      const existing = flagsByToken.get(dedupeKey);
      if (existing) {
        if (!existing.stepPositions.includes(step.position)) {
          existing.stepPositions.push(step.position);
        }
      } else {
        flagsByToken.set(dedupeKey, {
          kind: nonBraceKind,
          token: nonBraceToken,
          stepPositions: [step.position],
          terraEquivalent: null,
        });
      }
    }
  }

  const flags = [...flagsByToken.values()];
  for (const flag of flags) flag.stepPositions.sort((a, b) => a - b);
  return flags;
}

/**
 * The badge string for a sequence's flags, mirroring `sequence-readiness.ts`'s
 * `notReadyBadgeText` style: one phrase per KIND present, joined with `, `.
 * `null` when there is nothing to flag.
 */
export function tokenFlagBadgeText(flags: readonly TokenFlag[]): string | null {
  if (flags.length === 0) return null;

  const parts: string[] = [];

  const gongUnmapped = flags.filter((f) => f.kind === 'gong_unmapped');
  if (gongUnmapped.length > 0) {
    parts.push(`Gong tokens need Terra conversion (${gongUnmapped.length})`);
  }

  if (flags.some((f) => f.kind === 'conditional')) {
    parts.push('Unsupported conditional');
  }

  const unknownTokens = flags.filter((f) => f.kind === 'unknown');
  if (unknownTokens.length > 0) {
    const label = unknownTokens.length === 1 ? 'Unknown token' : 'Unknown tokens';
    parts.push(`${label} ${unknownTokens.map((f) => `{{${f.token}}}`).join(', ')}`);
  }

  // The five non-`{{}}` classes — each phrase names the step(s) so the badge
  // reads as an instruction ("go fix step 2"), not a bare token dump.
  const stepSuffix = (f: TokenFlag): string =>
    f.stepPositions.length > 0 ? ` (step ${f.stepPositions.map((p) => p + 1).join(', ')})` : '';
  // The kind→delimiter mapping itself now lives in ONE place, `wrapNonBraceToken`
  // (Devin D6) — this just narrows `f.kind` to the five-member subset it
  // accepts (already guaranteed by the `.filter` predicates below) and
  // appends the step suffix.
  const delimited = (f: TokenFlag & { kind: NonBraceDefectKind }): string =>
    `${wrapNonBraceToken(f.kind, f.token)}${stepSuffix(f)}`;

  const singleBrace = flags.filter(
    (f): f is TokenFlag & { kind: 'single_brace' } => f.kind === 'single_brace',
  );
  if (singleBrace.length > 0) {
    parts.push(`Placeholder left in copy: ${singleBrace.map((f) => delimited(f)).join(', ')}`);
  }

  const doubleBracket = flags.filter(
    (f): f is TokenFlag & { kind: 'double_bracket' } => f.kind === 'double_bracket',
  );
  if (doubleBracket.length > 0) {
    parts.push(`Placeholder left in copy: ${doubleBracket.map((f) => delimited(f)).join(', ')}`);
  }

  const angleBracket = flags.filter(
    (f): f is TokenFlag & { kind: 'angle_bracket' } => f.kind === 'angle_bracket',
  );
  if (angleBracket.length > 0) {
    parts.push(`Placeholder left in copy: ${angleBracket.map((f) => delimited(f)).join(', ')}`);
  }

  const deadHref = flags.filter((f) => f.kind === 'dead_href');
  if (deadHref.length > 0) {
    parts.push(`Link has no destination: ${deadHref.map((f) => `${f.token}${stepSuffix(f)}`).join(', ')}`);
  }

  const scaffolding = flags.filter((f) => f.kind === 'scaffolding');
  if (scaffolding.length > 0) {
    parts.push(`Planning notes left in copy: ${scaffolding.map((f) => `${f.token}${stepSuffix(f)}`).join(', ')}`);
  }

  return parts.join(', ');
}
